/* 나의 시공사례 — /agents/portfolio (왼쪽 메뉴 자료 → 나의 시공사례). 우리 회사가 만든 AI 시공사례 조회 전용.
   만들기는 "시공사례 등록"(/agents/portfolio/write), 퍼가기는 "블로그로 퍼가기"(/agents/portfolio/sns).
   현장명으로 검색하면 그 현장 사진이 한 장이라도 들어간 시공사례가 모두 나온다(여러 현장 사진으로 만든 모음집 포함 — TS_PORTFOLIO_PROJECT_SITE) */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Badge, Button, DateRangePicker, EmptyState, ErrorState, FilterChip, Page, PageHeader, SearchCombo, Skeleton, type DateRangeValue } from '@indipia/ui-core';
import { showcaseApi, type ShowcaseListItem } from './api/showcaseApi';
import { ConfirmDialog, Toast } from './components/Common';
import { IcGlobe, IcLock } from './components/icons';
import { PageThumb } from './components/render';
import { CARD, CARD_GRID, CARD_THUMB, chipRow, cx, pressable, relTime } from './components/ui';
import { loadShowcaseFonts } from './model/constants';
import { ed, toast, useEd } from './store/editor';
import { loadSites, photoState } from './store/photos';

const caseUrl = (id: number) => `/agents/portfolio/showcase/${id}`;
type Scope = 'all' | 'multi';
/** 날짜 비교용 'YYYY-MM-DD'(내 컴퓨터 날짜 기준) */
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function CasesPage() {
  useEd();
  const navigate = useNavigate();
  const [cases, setCases] = useState<ShowcaseListItem[] | null>(null);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [scope, setScope] = useState<Scope>('all');
  // 검색 — 나의 사진첩 현장 목록과 같은 모양: 검색어(제목·현장 이름) [검색] · 기간(수정일), 아래 줄에 범위 칩
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [range, setRange] = useState<DateRangeValue>({ start: null, end: null });
  const [del, setDel] = useState<ShowcaseListItem | null>(null);

  useEffect(() => { loadShowcaseFonts(); void loadSites(); }, []);
  useEffect(() => {
    let alive = true;
    setError(false);
    showcaseApi.list().then((l) => alive && setCases(l)).catch(() => { if (alive) { setCases([]); setError(true); } });
    return () => { alive = false; };
  }, [reload]);

  /** 현장 이름 — 인디포토 현장 목록(사진이 있는 현장)에서. 모르면 "현장 번호" */
  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    (photoState.sites || []).forEach((s) => m.set(s.siteId, s.siteName || `현장 ${s.siteId}`));
    return (id: number) => m.get(id) || `현장 ${id}`;
  }, [photoState.sites]);

  const words = applied.split(/\s+/).filter(Boolean);
  const from = range.start ? ymd(range.start) : '', to = range.end ? ymd(range.end) : from;
  /** 범위 칩을 뺀 조건(검색어·기간) — 칩 숫자는 이 조건 안에서 센다 */
  const base = (cases || []).filter((c) => {
    if (from) { const d = ymd(new Date(c.updatedAt)); if (d < from || d > to) return false; }
    if (words.length) {
      const hay = [c.title, ...(c.sites || []).map((s) => siteName(s.siteId))].join(' ').toLowerCase();
      if (!words.every((w) => hay.includes(w.toLowerCase()))) return false;
    }
    return true;
  });
  const list = scope === 'multi' ? base.filter((c) => (c.sites || []).length > 1) : base;
  const filtered = !!(words.length || from || scope !== 'all');
  const resetSearch = () => { setKeyword(''); setApplied(''); setRange({ start: null, end: null }); setScope('all'); };

  const setVis = async (c: ShowcaseListItem) => {
    try {
      await showcaseApi.setPublic(c.projectId, c.publicYn !== 'Y');
      toast(c.publicYn !== 'Y' ? '공개로 바꿨어요 — 링크가 있으면 누구나 볼 수 있어요' : '비공개로 바꿨어요 — 나만 볼 수 있어요');
      if (ed.projectId === c.projectId) ed.publicYn = c.publicYn === 'Y' ? 'N' : 'Y';
      setReload((n) => n + 1);
    } catch { toast('공개 설정을 바꾸지 못했어요'); }
  };
  const copyLink = async (url: string) => {
    try { await navigator.clipboard.writeText(url); toast('공유 링크를 복사했어요'); } catch { window.prompt('이 주소를 복사하세요', url); }
  };
  const doDelete = async (c: ShowcaseListItem) => {
    try {
      await showcaseApi.remove(c.projectId);
      if (ed.projectId === c.projectId) { ed.projectId = null; ed.pages = []; ed.lastSaved = ''; }
      toast(`'${c.title}'을(를) 지웠어요`);
      setReload((n) => n + 1);
    } catch { toast('지우지 못했어요 — 잠시 뒤 다시 해 주세요'); }
  };

  /** 카드의 현장 표시 — 하나면 현장 이름, 여러 곳이면 "여러 현장 · N곳"(마우스를 올리면 현장 목록) */
  const siteLine = (c: ShowcaseListItem) => {
    const s = c.sites || [];
    if (!s.length) return <span className="truncate">현장 미지정</span>;
    if (s.length === 1) return <span className="truncate" title={siteName(s[0].siteId)}>{siteName(s[0].siteId)}</span>;
    return <span className="truncate" title={s.map((x) => `${siteName(x.siteId)} (사진 ${x.photoCount}장)`).join('\n')}>여러 현장 · {s.length}곳</span>;
  };

  let body;
  if (error) body = <ErrorState title="나의 시공사례를 불러오지 못했어요" onRetry={() => setReload((n) => n + 1)} />;
  else if (!cases) body = <div className={CARD_GRID} aria-busy="true">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="block" className="aspect-[3/4] w-full" />)}</div>;
  else if (!cases.length) {
    body = (
      <EmptyState title="아직 만든 시공사례가 없어요" description="템플릿을 고르고 현장 사진을 고르면 AI가 시공사례를 만들어 드려요."
        action={<Button onClick={() => navigate('/agents/portfolio/write')}>시공사례 등록</Button>} />
    );
  } else if (!list.length) {
    body = <EmptyState title="조건에 맞는 시공사례가 없어요" description="검색어나 기간을 바꾸거나 조건을 지워 보세요."
      action={<Button variant="etc" onClick={resetSearch}>조건 지우기</Button>} />;
  } else {
    body = (
      <div className={CARD_GRID}>
        {list.map((c) => {
          const pub = c.publicYn === 'Y', multi = (c.sites || []).length > 1;
          return (
            <div key={c.projectId} className={cx(CARD, 'group cursor-pointer rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-group')}
              aria-label={`${c.title} 열기`} {...pressable(() => navigate(caseUrl(c.projectId)))}>
              <span className={CARD_THUMB}><PageThumb page={c.cover} width="fill" /></span>
              <b className="mx-01 mt-02 truncate text-body-sm font-semibold" title={c.title}>{c.title}</b>
              <span className="mx-01 flex min-w-0 items-center gap-xs text-caption text-text-secondary">
                {multi && <Badge status="info">모음</Badge>}{siteLine(c)}
              </span>
              <span className="mx-01 text-caption text-text-secondary">{c.pageCount}페이지 · {relTime(c.updatedAt)} 수정{pub ? ` · 조회 ${c.viewCount}` : ''}</span>
              <div className="flex flex-wrap items-center gap-xs" onClick={(ev) => ev.stopPropagation()} onKeyDown={(ev) => ev.stopPropagation()}>
                <Button size="sm" variant={pub ? 'secondary' : 'etc'} title={`눌러서 ${pub ? '비공개' : '공개'}로 바꾸기`} onClick={() => setVis(c)}>
                  {pub ? <><IcGlobe size={16} />공개</> : <><IcLock size={16} />비공개</>}
                </Button>
                {pub && c.publicUrl && <Button size="sm" variant="ghost" title="공유 링크 복사" onClick={() => copyLink(c.publicUrl!)}>링크</Button>}
                <Button size="sm" variant="ghost" className="hover:text-error" title="삭제" onClick={() => setDel(c)}>삭제</Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
  const multiCount = base.filter((c) => (c.sites || []).length > 1).length;
  return (
    <div data-showcase className="h-full text-text">
      <Page layout="doc" width="full">
        <PageHeader title="나의 시공사례" subtitle="우리 회사가 만든 AI 시공사례예요. 현장명으로 검색하면 그 현장 사진이 들어간 모음집도 함께 나와요."
          extra={<Button onClick={() => navigate('/agents/portfolio/write')}>시공사례 등록</Button>} />
        {!!cases?.length && (
          /* 검색 영역 — 나의 사진첩(현장 목록)과 같은 구성: 검색 조합 · 기간, 아래에 기준 안내, 맨 아래 범위 칩 */
          <div className="mb-06 flex flex-col gap-sm rounded-xl border border-border bg-surface-raised p-05" role="search" aria-label="나의 시공사례 검색">
            <div className="flex flex-wrap items-center gap-sm">
              <SearchCombo className="min-w-0 flex-1" value={keyword} maxLength={50} placeholder="시공사례 제목 · 현장명 검색" role="searchbox"
                onChange={(v) => setKeyword(v.slice(0, 50))} onSearch={() => setApplied(keyword.trim())} onClear={() => { setKeyword(''); setApplied(''); }} />
              <DateRangePicker value={range} onChange={setRange} placeholder="전체 기간" aria-label="수정일 기간" />
              {filtered && <Button variant="etc" onClick={resetSearch}>초기화</Button>}
            </div>
            <p className="m-0 text-caption text-text-secondary">기간은 시공사례를 마지막으로 고친 날 기준이에요. 현장명으로 검색하면 그 현장 사진이 들어간 모음집도 함께 나와요.</p>
            <div className={chipRow} role="group" aria-label="시공사례 범위">
              <FilterChip active={scope === 'all'} aria-pressed={scope === 'all'} onClick={() => setScope('all')} count={base.length}>전체</FilterChip>
              <FilterChip active={scope === 'multi'} aria-pressed={scope === 'multi'} onClick={() => setScope('multi')} count={multiCount}>여러 현장 모음</FilterChip>
            </div>
          </div>
        )}
        {body}
      </Page>
      {del && <ConfirmDialog title="이 시공사례를 지울까요?" desc={`'${del.title}' — 지우면 되돌릴 수 없고, 공개 링크도 바로 막혀요.`} okLabel="삭제" danger
        onClose={(ok) => { const c = del; setDel(null); if (ok) void doDelete(c); }} />}
      <Toast />
    </div>
  );
}
